import { useMemo, useState } from "react";
import { Box, ChevronRight, CircleDot, ExternalLink, Gauge, PackageSearch, ShieldCheck, Wrench } from "lucide-react";
import type { Asset, Part, PlantState, VerificationState } from "./data";
import "./extruderVisual.css";

type HotspotId = "feed_system" | "drive_motor" | "gearbox" | "screw" | "barrel" | "heater_zones" | "cooling_fans" | "die_head";

type HotspotDefinition = {
  id: HotspotId;
  label: string;
  keywords: string[];
  preferChild?: boolean;
};

const HOTSPOTS: HotspotDefinition[] = [
  { id: "feed_system", label: "Hopper / Feed System", keywords: ["hopper", "feed system", "material feed", "feeder"] },
  { id: "drive_motor", label: "Drive Motor", keywords: ["drive motor", "main motor", "motor"], preferChild: true },
  { id: "gearbox", label: "Gearbox", keywords: ["gearbox", "gear box", "reducer"], preferChild: true },
  { id: "screw", label: "Screw", keywords: ["screw set", "screw"], preferChild: true },
  { id: "barrel", label: "Barrel", keywords: ["barrel"], preferChild: true },
  { id: "heater_zones", label: "Heater Zones", keywords: ["heater zone", "heater", "heating"], preferChild: true },
  { id: "cooling_fans", label: "Cooling Fans / Blowers", keywords: ["cooling fan", "blower", "cooling assembly", "barrel cooling"], preferChild: true },
  { id: "die_head", label: "Die / Head", keywords: ["die head", "die/head", "die", "head"], preferChild: true },
];

function searchable(asset: Asset) {
  return `${asset.tag} ${asset.name} ${asset.type}`.toLowerCase();
}

function matchAsset(definition: HotspotDefinition, parent: Asset, lineAssets: Asset[]) {
  const active = lineAssets.filter((candidate) => candidate.status !== "Retired" && candidate.id !== parent.id);
  const children = active.filter((candidate) => candidate.parentId === parent.id);
  const pools = definition.preferChild ? [children, active] : [active, children];

  for (const pool of pools) {
    const match = pool.find((candidate) => definition.keywords.some((keyword) => searchable(candidate).includes(keyword)));
    if (match) return match;
  }
  return undefined;
}

function stateClass(asset?: Asset, selected = false) {
  if (selected) return "selected";
  if (!asset) return "unmapped";
  if (asset.verificationState === "Verified") return "verified";
  if (asset.verificationState === "Needs Research") return "research";
  return "incomplete";
}

function statusLabel(state?: VerificationState) {
  return state ?? "Not documented";
}

function partLabel(part: Part) {
  return part.internalPn || part.oemPn || part.description;
}

export default function ExtruderVisual({ db, asset, onOpenAsset }: {
  db: PlantState;
  asset: Asset;
  onOpenAsset: (assetId: string) => void;
}) {
  const lineAssets = useMemo(() => db.assets.filter((candidate) => candidate.lineId === asset.lineId), [db.assets, asset.lineId]);
  const mapping = useMemo(() => new Map(HOTSPOTS.map((definition) => [definition.id, matchAsset(definition, asset, lineAssets)])), [asset, lineAssets]);
  const [selectedId, setSelectedId] = useState<HotspotId>("drive_motor");

  const selectedDefinition = HOTSPOTS.find((item) => item.id === selectedId)!;
  const selectedAsset = mapping.get(selectedId);
  const bomRows = selectedAsset
    ? selectedAsset.bom.map((item) => ({ item, part: db.parts.find((part) => part.id === item.partId) })).filter((row): row is { item: Asset["bom"][number]; part: Part } => Boolean(row.part))
    : [];
  const alternateParts = Array.from(new Set(bomRows.flatMap(({ part }) => part.alternates))).map((id) => db.parts.find((part) => part.id === id)).filter((part): part is Part => Boolean(part));
  const preferredVendors = Array.from(new Set(bomRows.map(({ part }) => part.preferredVendorId).filter(Boolean))).map((id) => db.vendors.find((vendor) => vendor.id === id)).filter(Boolean);

  const mappedAssets = HOTSPOTS.map((item) => mapping.get(item.id)).filter(Boolean) as Asset[];
  const verified = mappedAssets.filter((item) => item.verificationState === "Verified").length;
  const research = mappedAssets.filter((item) => item.verificationState === "Needs Research").length;
  const unmapped = HOTSPOTS.length - mappedAssets.length;

  const cls = (id: HotspotId) => `ev-hotspot ${stateClass(mapping.get(id), selectedId === id)}`;
  const select = (id: HotspotId) => () => setSelectedId(id);
  const keyboardSelect = (id: HotspotId) => (event: React.KeyboardEvent<SVGGElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setSelectedId(id);
    }
  };

  return <div className="ev-shell">
    <div className="ev-summary">
      <div><span>Mapped</span><strong>{mappedAssets.length}/{HOTSPOTS.length}</strong></div>
      <div><span>Verified</span><strong>{verified}</strong></div>
      <div><span>Needs research</span><strong>{research}</strong></div>
      <div><span>Not documented</span><strong>{unmapped}</strong></div>
    </div>

    <div className="ev-layout">
      <section className="ev-canvas-card">
        <div className="ev-canvas-head">
          <div><span className="eyebrow">CURRENT CONFIGURATION MAP</span><h2>{asset.name}</h2><p>Tap a highlighted assembly to inspect what is currently documented as installed.</p></div>
          <div className="ev-legend"><span className="verified">Verified</span><span className="research">Needs research</span><span className="incomplete">Incomplete</span><span className="unmapped">Not mapped</span></div>
        </div>

        <div className="ev-svg-wrap">
          <svg className="ev-svg" viewBox="0 0 1100 600" role="img" aria-label="Interactive extruder configuration schematic">
            <defs>
              <linearGradient id="machineBody" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#25394a"/><stop offset="1" stopColor="#12202d"/></linearGradient>
              <pattern id="screwPattern" width="34" height="28" patternUnits="userSpaceOnUse"><path d="M0 25 L17 3 L34 25" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round"/></pattern>
            </defs>

            <path d="M95 492 H975" className="ev-frame-line" />
            <path d="M170 492 V530 M880 492 V530" className="ev-frame-line" />
            <path d="M140 530 H920" className="ev-frame-line" />

            <g className={cls("gearbox")} role="button" tabIndex={0} aria-label={`Gearbox: ${statusLabel(mapping.get("gearbox")?.verificationState)}`} onClick={select("gearbox")} onKeyDown={keyboardSelect("gearbox")}>
              <rect className="ev-shape" x="115" y="245" width="190" height="185" rx="18" />
              <circle className="ev-detail" cx="175" cy="332" r="46"/><circle className="ev-detail" cx="240" cy="315" r="31"/>
              <path className="ev-detail" d="M305 328 H365" />
              <rect className="ev-hit" x="95" y="225" width="240" height="230" rx="22" />
              <text className="ev-label" x="210" y="215">GEARBOX</text>
            </g>

            <g className={cls("drive_motor")} role="button" tabIndex={0} aria-label={`Drive motor: ${statusLabel(mapping.get("drive_motor")?.verificationState)}`} onClick={select("drive_motor")} onKeyDown={keyboardSelect("drive_motor")}>
              <rect className="ev-shape" x="185" y="445" width="205" height="78" rx="30" />
              <path className="ev-detail" d="M215 457 V510 M238 457 V510 M262 457 V510" />
              <path className="ev-detail" d="M390 484 H430" />
              <rect className="ev-hit" x="165" y="425" width="285" height="120" rx="25" />
              <text className="ev-label" x="285" y="575">DRIVE MOTOR</text>
            </g>

            <g className={cls("feed_system")} role="button" tabIndex={0} aria-label={`Hopper and feed system: ${statusLabel(mapping.get("feed_system")?.verificationState)}`} onClick={select("feed_system")} onKeyDown={keyboardSelect("feed_system")}>
              <path className="ev-shape" d="M355 72 H525 L490 175 H390 Z" />
              <rect className="ev-shape" x="420" y="175" width="40" height="95" rx="8" />
              <rect className="ev-hit" x="330" y="48" width="220" height="235" rx="20" />
              <text className="ev-label" x="440" y="42">HOPPER / FEED</text>
            </g>

            <g className={cls("barrel")} role="button" tabIndex={0} aria-label={`Barrel: ${statusLabel(mapping.get("barrel")?.verificationState)}`} onClick={select("barrel")} onKeyDown={keyboardSelect("barrel")}>
              <rect className="ev-shape" x="365" y="280" width="540" height="112" rx="48" fill="url(#machineBody)" />
              <rect className="ev-hit" x="350" y="265" width="570" height="145" rx="52" />
              <text className="ev-label" x="625" y="253">BARREL</text>
            </g>

            <g className={cls("screw")} role="button" tabIndex={0} aria-label={`Screw: ${statusLabel(mapping.get("screw")?.verificationState)}`} onClick={select("screw")} onKeyDown={keyboardSelect("screw")}>
              <rect className="ev-screw" x="392" y="311" width="485" height="52" rx="25" fill="url(#screwPattern)" />
              <path className="ev-detail" d="M370 337 H895" />
              <rect className="ev-hit" x="380" y="297" width="510" height="80" rx="35" />
              <text className="ev-label" x="630" y="430">SCREW</text>
            </g>

            <g className={cls("heater_zones")} role="button" tabIndex={0} aria-label={`Heater zones: ${statusLabel(mapping.get("heater_zones")?.verificationState)}`} onClick={select("heater_zones")} onKeyDown={keyboardSelect("heater_zones")}>
              {[470, 555, 640, 725, 810].map((x) => <rect key={x} className="ev-shape ev-band" x={x} y="272" width="28" height="128" rx="7" />)}
              <rect className="ev-hit" x="450" y="250" width="410" height="170" rx="25" />
              <text className="ev-label" x="660" y="205">HEATER ZONES</text>
            </g>

            <g className={cls("cooling_fans")} role="button" tabIndex={0} aria-label={`Cooling fans and blowers: ${statusLabel(mapping.get("cooling_fans")?.verificationState)}`} onClick={select("cooling_fans")} onKeyDown={keyboardSelect("cooling_fans")}>
              {[520, 620, 720, 820].map((x) => <g key={x}><circle className="ev-shape" cx={x} cy="458" r="31"/><path className="ev-detail" d={`M${x} 438 L${x+13} 463 L${x-14} 462 Z M${x-17} 469 L${x+10} 468 L${x} 445 Z`} /></g>)}
              <rect className="ev-hit" x="475" y="415" width="400" height="90" rx="20" />
              <text className="ev-label" x="675" y="540">COOLING / BLOWERS</text>
            </g>

            <g className={cls("die_head")} role="button" tabIndex={0} aria-label={`Die and head: ${statusLabel(mapping.get("die_head")?.verificationState)}`} onClick={select("die_head")} onKeyDown={keyboardSelect("die_head")}>
              <path className="ev-shape" d="M900 295 H968 L1020 318 V357 L968 380 H900 Z" />
              <path className="ev-detail" d="M930 320 H1005 M930 355 H1005" />
              <rect className="ev-hit" x="885" y="275" width="155" height="125" rx="22" />
              <text className="ev-label" x="960" y="255">DIE / HEAD</text>
            </g>
          </svg>
        </div>

        <div className="ev-mobile-selector">
          {HOTSPOTS.map((hotspot) => {
            const mapped = mapping.get(hotspot.id);
            return <button key={hotspot.id} className={`${stateClass(mapped, selectedId === hotspot.id)}`} onClick={() => setSelectedId(hotspot.id)}><span>{hotspot.label}</span><small>{mapped ? mapped.tag : "Not documented"}</small></button>;
          })}
        </div>
      </section>

      <aside className="ev-drawer">
        <div className="ev-drawer-head">
          <div><span className="eyebrow">SELECTED ASSEMBLY</span><h2>{selectedDefinition.label}</h2></div>
          <span className={`ev-state-pill ${stateClass(selectedAsset)}`}>{statusLabel(selectedAsset?.verificationState)}</span>
        </div>

        {!selectedAsset ? <div className="ev-unmapped-card">
          <CircleDot size={28}/><h3>Not documented yet</h3><p>This visual zone is intentionally unlinked until the plant walkdown identifies the actual installed component.</p>
        </div> : <>
          <div className="ev-installed-card">
            <div className="ev-installed-title"><Wrench size={18}/><div><strong>{selectedAsset.name}</strong><span className="mono">{selectedAsset.tag}</span></div></div>
            <dl>
              <dt>Manufacturer</dt><dd>{selectedAsset.manufacturer || "Unknown"}</dd>
              <dt>Model</dt><dd>{selectedAsset.model || "Unknown"}</dd>
              <dt>Serial</dt><dd>{selectedAsset.serial || "Unknown"}</dd>
              <dt>Status</dt><dd>{selectedAsset.status}</dd>
              <dt>Criticality</dt><dd>{selectedAsset.criticality}</dd>
              <dt>Guide Ti</dt><dd>{selectedAsset.guideTiId || "Not linked"}</dd>
            </dl>
            <div className="ev-complete"><span>Documentation</span><strong>{selectedAsset.completeness}%</strong><div><i style={{ width: `${selectedAsset.completeness}%` }}/></div></div>
            <button className="ev-open" onClick={() => onOpenAsset(selectedAsset.id)}>Open full technical passport <ChevronRight size={15}/></button>
          </div>

          <section className="ev-section"><h3><Gauge size={16}/> Key specifications</h3>
            {selectedAsset.specs.length ? selectedAsset.specs.slice(0, 6).map((spec) => <div className="ev-row" key={spec.label}><span>{spec.label}</span><strong>{spec.value}{spec.unit ? ` ${spec.unit}` : ""}</strong></div>) : <p className="ev-empty">No specifications documented yet.</p>}
          </section>

          <section className="ev-section"><h3><PackageSearch size={16}/> Installed / service parts</h3>
            {bomRows.length ? bomRows.map(({ item, part }) => <div className="ev-part" key={`${part.id}-${item.locationNote || ""}`}><div><strong>{partLabel(part)}</strong><span>{part.description} · qty {item.qty}{item.locationNote ? ` · ${item.locationNote}` : ""}</span></div><small>Stock {part.stockQty} / min {part.minQty}</small></div>) : <p className="ev-empty">No BOM items linked to this assembly.</p>}
          </section>

          <section className="ev-section"><h3><ShieldCheck size={16}/> Potential replacements</h3>
            {alternateParts.length ? alternateParts.map((part) => <div className="ev-part" key={part.id}><div><strong>{partLabel(part)}</strong><span>{part.description}</span></div><small>{part.manufacturer}</small></div>) : <p className="ev-empty">No approved alternates documented for its BOM parts.</p>}
          </section>

          <section className="ev-section"><h3><Box size={16}/> Preferred sources</h3>
            {preferredVendors.length ? preferredVendors.map((vendor) => vendor && <div className="ev-part" key={vendor.id}><div><strong>{vendor.name}</strong><span>{vendor.contact || vendor.website || "Supplier record"}</span></div>{vendor.website && <ExternalLink size={13}/>}</div>) : <p className="ev-empty">No preferred supplier is mapped to the selected BOM parts.</p>}
          </section>
        </>}
      </aside>
    </div>
  </div>;
}
